import { useState } from 'react'
import { UserCircle, Briefcase, CalendarCheck, LayoutDashboard, MessageSquare, ClipboardList } from 'lucide-react'

interface OnboardingModalProps {
  open: boolean
  onDismiss: () => void
}

const steps = [
  {
    icon: UserCircle,
    badge: 'Getting Started',
    title: 'Welcome to Interview Dress Rehearsal!',
    lines: [
      'This platform helps you practice and ace real-world interviews with experienced interviewers.',
      'You\'ll get structured mock sessions, detailed feedback, and performance insights — all in one place.',
      'Let\'s take a quick tour so you know exactly where to go.',
    ],
  },
  {
    icon: Briefcase,
    badge: 'Step 1 · My Profile',
    title: 'Complete Your Profile First',
    lines: [
      'Head to My Profile and fill in your Personal Info, Professional Details, and Preferences.',
      'Your profile must be complete before you can schedule sessions — it helps us match you with the right interviewer.',
      'It only takes 2 minutes and unlocks everything on the platform.',
    ],
  },
  {
    icon: CalendarCheck,
    badge: 'Step 2 · Schedule a Session',
    title: 'Book a Mock Interview',
    lines: [
      'Go to Schedule a Session, choose your target role and experience level, then pick an available interviewer and time slot.',
      'Sessions are conducted over Zoom — you\'ll receive a link after booking.',
      'You can schedule as many sessions as you need to build confidence.',
    ],
  },
  {
    icon: LayoutDashboard,
    badge: 'Step 3 · Dashboard',
    title: 'Track Everything on Your Dashboard',
    lines: [
      'Your Dashboard shows upcoming interviews, Zoom join links (available 45 min before start), and your progress stats.',
      'You\'ll also see a snapshot of your most recent feedback right on the home screen.',
      'Check here before every session so you never miss a booking.',
    ],
  },
  {
    icon: MessageSquare,
    badge: 'Step 4 · View Feedback',
    title: 'Review Your Interview Feedback',
    lines: [
      'After each session, head to View Feedback to see your detailed evaluation scores.',
      'You\'ll get written notes from the interviewer covering communication, problem-solving, and role-fit.',
      'Use this feedback to identify strengths and areas to improve before your next session.',
    ],
  },
  {
    icon: ClipboardList,
    badge: 'Step 5 · Manage Interviews',
    title: 'Manage All Your Interviews',
    lines: [
      'Manage Interviews gives you a full history of past and upcoming sessions in one place.',
      'Need to change your schedule? You can reschedule any upcoming interview directly from here.',
      'You\'re all set — let\'s start preparing for your dream role!',
    ],
  },
]

export default function OnboardingModal({ open, onDismiss }: OnboardingModalProps) {
  const [step, setStep] = useState(0)

  if (!open) return null

  const current = steps[step]
  const Icon = current.icon
  const isLast = step === steps.length - 1
  const isFirst = step === 0

  const handleNext = () => {
    if (isLast) onDismiss()
    else setStep(s => s + 1)
  }

  return (
    <div className="modal-overlay" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>

        <div className="modal-body" style={{ paddingBottom: 0 }}>
          {/* Icon + badge */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
            <div className="ob-icon-wrap">
              <Icon size={36} strokeWidth={1.5} />
            </div>
            <span className="ob-badge">{current.badge}</span>
          </div>

          {/* Title */}
          <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary-dark)', textAlign: 'center', marginBottom: 16 }}>
            {current.title}
          </h2>

          {/* Content lines */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {current.lines.map((line, i) => (
              <p key={i} style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0, textAlign: 'center' }}>
                {line}
              </p>
            ))}
          </div>

          {/* Progress dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 28 }}>
            {steps.map((_, i) => (
              <span key={i} className={i === step ? 'ob-dot ob-dot-active' : 'ob-dot'} />
            ))}
          </div>
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          {/* Skip link */}
          <button
            type="button"
            onClick={onDismiss}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)', padding: '8px 0' }}
          >
            Skip tour
          </button>

          {/* Prev / Next */}
          <div style={{ display: 'flex', gap: 8 }}>
            {!isFirst && (
              <button type="button" className="btn btn-secondary" onClick={() => setStep(s => s - 1)}>
                Previous
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={handleNext}>
              {isLast ? 'Get Started' : 'Next'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .ob-icon-wrap {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: var(--accent-gold, #d4af37);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary-dark, #1e2432);
          margin-bottom: 10px;
        }
        .ob-badge {
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--accent-gold, #d4af37);
          background: rgba(212, 175, 55, 0.12);
          padding: 3px 10px;
          border-radius: 20px;
        }
        .ob-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #d1d5db;
          display: inline-block;
          transition: background 0.2s;
        }
        .ob-dot-active {
          background: var(--accent-gold, #d4af37);
          width: 22px;
          border-radius: 4px;
        }
      `}</style>
    </div>
  )
}
